import { GridLegacy as Grid } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { LinearProgress } from 'lead-list/components/LinearProgress';
import { Select } from 'lead-list/components/Select';
import { RightArrowIcon } from 'lead-list/icons';
import { FC, ReactElement, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { ListType } from 'types/apollo/globalTypes';
import messages from '../messages';
import { leadListMappings } from './leadListMappings';

interface LeadListColumnTypeMappingProps {
  columnName: string;
  error?: boolean;
  onChange: (val: { [key: string]: string }) => void;
  value?: string;
  currentMappings: string[];
}

const LeadListColumnTypeMapping: FC<LeadListColumnTypeMappingProps> = ({
  columnName,
  error,
  onChange,
  value = 'unmapped',
  currentMappings,
}) => {
  const { formatMessage } = useIntl();

  const handleChange = useCallback(
    (event) => {
      onChange({ [columnName]: event.target.value });
    },
    [columnName, onChange],
  );

  return (
    <Grid container item alignItems="center">
      <Grid item xs={4} sx={{ pl: 1 }}>
        <Typography variant="body1" noWrap>
          {columnName}
        </Typography>
      </Grid>
      <Grid item xs={2}>
        <RightArrowIcon />
      </Grid>
      <Grid item xs={6}>
        <Select error={error} onChange={handleChange} value={value} variant="outlined">
          <MenuItem value="unmapped">
            <Typography color="textSecondary">
              <em>{formatMessage(messages.selectField)}</em>
            </Typography>
          </MenuItem>
          {leadListMappings.map((mapping) => (
            <MenuItem
              key={mapping.value}
              value={mapping.value}
              disabled={currentMappings.includes(mapping.value) && value !== mapping.value}
            >
              {mapping.name}
            </MenuItem>
          ))}
        </Select>
      </Grid>
    </Grid>
  );
};

interface ListColumnMapperProps {
  headers: string[];
  onSetMappings: (value: { [key: string]: string }) => void;
  mappings: { [key: string]: string };
  hasPhone?: boolean;
  hasName?: boolean;
  hasDuplicate?: boolean;
  hasCreated?: boolean;
  listType: ListType;
}

export const ListColumnMapper = ({
  headers,
  onSetMappings,
  mappings,
  hasPhone,
  hasName,
  hasDuplicate,
  hasCreated,
  listType,
}: ListColumnMapperProps): ReactElement => {
  const { formatMessage } = useIntl();

  const currentMappings = headers.map((h) => mappings[h]).filter(Boolean) as string[];

  const checkForDuplicates = (
    currentValue: string | undefined,
    values: { [k: string]: string },
  ) => {
    if (!currentValue || currentValue === 'unmapped') return false;
    const count = headers.reduce((acc, h) => {
      const v = values[h];
      return acc + (v && v !== 'unmapped' && v === currentValue ? 1 : 0);
    }, 0);
    return count > 1;
  };

  const mappingComponent = headers.map((columnName) => {
    const isDuplicated = mappings[columnName]
      ? checkForDuplicates(mappings[columnName], mappings)
      : false;
    return (
      <LeadListColumnTypeMapping
        columnName={columnName}
        error={isDuplicated}
        key={columnName}
        onChange={onSetMappings}
        value={mappings[columnName]}
        currentMappings={currentMappings}
      />
    );
  });

  const progress = (function () {
    if (hasDuplicate) return 0;
    if (listType === ListType.PROSPECTIVE) {
      if (!hasPhone) return 33;
      if (!hasName) return 66;
      return 100;
    }
    if (!hasPhone) return 0;
    if (listType === ListType.CLOSE) {
      if (!hasCreated) return 66;
    }
    return 100;
  })();

  const message = (function () {
    if (hasDuplicate) return formatMessage(messages.confirmationDialogueDuplicate);
    if (!hasPhone) return formatMessage(messages.confirmationDialoguePhoneMissing);
    if (listType === ListType.PROSPECTIVE && !hasName)
      return formatMessage(messages.confirmationDialogueNameMissing);
    if (listType === ListType.CLOSE && !hasCreated)
      return formatMessage(messages.confirmationDialogueNoCreated);
    return formatMessage(messages.confirmationDialoguePerfection);
  })();

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 4 }}>
        {headers.length} {formatMessage(messages.columnsFound)}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="body1" color="textSecondary">
            {formatMessage(messages.csvColumns)}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body1" color="textSecondary">
            {formatMessage(messages.systemField)}
          </Typography>
        </Grid>
        {mappingComponent}
      </Grid>

      <Box alignItems="center" display="flex" flexDirection="column" mt={6}>
        <Box maxWidth="375px" width="100%" height={5}>
          <LinearProgress
            value={progress}
            variant="determinate"
            color={progress < 66 ? 'orange' : progress < 100 ? 'orange' : 'green'}
          />
        </Box>
        <Box mt={2}>
          <Typography variant="body1" color="textSecondary">
            {message}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
